/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { queueQuotesRequest } from './sharedThunks/quotesRequestPlanner';
import {
  buildScopesFromAssets,
  collectDataTransactions,
  extractAssetDateBoundsFromTransactions,
  normalizeDates,
} from './sharedThunks/quoteScopes';
import { globalBaselineView } from './globalSelectors';
import { assetBalancesArray } from './data';

const utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

const baseDate = dayjs.utc().add(1, 'year').startOf('month').valueOf();

const initialState = {
  baseDate: baseDate.valueOf(),
  dashboardMode: 'dashboard', // 'projects'
  slider: {
    min: new Date('2015-01-01T00:00:00Z').valueOf(),
    max: new Date('2031-01-01T00:00:00Z').valueOf(),
  },
};

function getProviderAssetIdKey(providerAssetId) {
  if (!providerAssetId) return '';
  if (typeof providerAssetId === 'object') return JSON.stringify(providerAssetId);
  return String(providerAssetId);
}

function getScopedQuoteAssetsFromBalances(balances = []) {
  const seen = new Set();
  const scopedAssets = [];

  (balances || []).forEach((balance) => {
    const assetId = balance?.assetId;
    const currency = balance?.transactionCurrency || balance?.accountCurrency;
    const providerAssetId = balance?.providerAssetId || null;
    if (!assetId || !currency) return;

    const key = `${assetId}|${getProviderAssetIdKey(providerAssetId)}|${currency}`;
    if (seen.has(key)) return;
    seen.add(key);
    scopedAssets.push({ assetId, providerAssetId, currency });
  });

  return scopedAssets;
}

const simulationSlice = createSlice({
  name: 'simulation',
  initialState,
  reducers: {
    setSimulationBaseDate(state, action) {
      const { newDate } = action.payload;

      // check if we are receiving a correct argument
      if (Number.isNaN(newDate)) throw new Error(`Error: setSimulationBaseDate expects a valid Number (got ${newDate} instead)`);

      // force set argument to be the midnight of the 1st calendar day of the month
      const transformedDate = dayjs.utc(newDate).startOf('month');

      state.baseDate = transformedDate.valueOf();
    },
    setDashboardMode(state, action) {
      const { dashboardMode } = action.payload;
      if (!['dashboard', 'projects'].includes(dashboardMode)) throw new Error(`Error: setDashboardMode expects a valid dashboardMode (got ${dashboardMode} instead)`);

      state.dashboardMode = dashboardMode;
    },
  },
});

export default simulationSlice.reducer;

// export all reducers as actions if needed
export const { setDashboardMode, setSimulationBaseDate } = simulationSlice.actions;

export const setSimulationBaseDateAndUpdateQuotes = createAsyncThunk('simulation/setSimulationBaseDateAndUpdateQuotes', async ({ newDate }, { dispatch, getState }) => {
  // check if we are receiving a correct argument
  // if null is passed as newDate, use the current baseDate and just get quotes (used when initialising Dashboard in project mode)
  if (newDate !== null && Number.isNaN(newDate.valueOf())) throw new Error(`Error: setSimulationBaseDate expects a Number or a null (got ${newDate} instead)`);

  const { baseline, referenceBaseline } = globalBaselineView(getState());

  let transformedDate;
  if (newDate) {
    // force-set argument to be the midnight of the 1st calendar day of the month
    transformedDate = dayjs.utc(newDate).startOf('month').valueOf();

    // await dispatch set simulation basedate (sliderDate)
    await dispatch(setSimulationBaseDate({ newDate: transformedDate }));
  }

  if (newDate === null) {
    transformedDate = getState().simulation?.baseDate;
  }

  // Baseline/slider updates invalidate a narrow quote scope:
  // refresh only currently relevant assets and numeric baseline/reference/current dates.
  const assets = getScopedQuoteAssetsFromBalances(assetBalancesArray(getState()));

  await queueQuotesRequest(
    {
      scopes: buildScopesFromAssets({
        assets,
        requiredDates: normalizeDates([baseline, referenceBaseline, transformedDate]),
        assetDateBounds: extractAssetDateBoundsFromTransactions(collectDataTransactions(getState())),
      }),
    },
    { dispatch, immediate: true },
  );

  return { transformedDate };
});
